import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Link,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  Fade,
  Slide,
  Zoom,
  Grow,
  keyframes,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd as RegisterIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Stars as SparklesIcon,
} from '@mui/icons-material';
import { login } from '../../store/slices/authSlice';
import { AppDispatch } from '../../store/store';
import czechTranslations from '../../translations/czech';

// 🎨 Animated backgrounds
const floatingAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-30px) rotate(120deg); }
  66% { transform: translateY(-60px) rotate(240deg); }
  100% { transform: translateY(0px) rotate(360deg); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.6), 0 0 60px rgba(99, 102, 241, 0.4); }
  100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); }
`;

const shimmerText = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const ModernLogin: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 🎭 Animation states
  const [showContent, setShowContent] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // 📝 Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 🎪 Staggered entrance animations
    setTimeout(() => setShowContent(true), 200);
    setTimeout(() => setShowForm(true), 600);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.email || !formData.password) {
      setError(czechTranslations.auth.login.errors.emailRequired);
      setLoading(false);
      return;
    }

    try {
      const result = await dispatch(login({
        email: formData.email,
        password: formData.password,
      }));

      if (login.fulfilled.match(result)) {
        navigate('/');
      } else {
        setError(czechTranslations.auth.login.errors.invalidCredentials);
      }
    } catch (err: any) {
      setError(err.message || czechTranslations.common.errors.general);
    }

    setLoading(false);
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
    }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #6366f1 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)',
          zIndex: 1,
        }
      }}
    >
      {/* ✨ Floating animated elements */}
      {[...Array(6)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: { xs: '60px', md: '100px' },
            height: { xs: '60px', md: '100px' },
            borderRadius: '50%',
            background: `linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)`,
            animation: `${floatingAnimation} ${6 + i * 2}s ease-in-out infinite`,
            animationDelay: `${i * 0.5}s`,
            zIndex: 1,
            top: `${20 + Math.random() * 60}%`,
            left: `${10 + Math.random() * 80}%`,
          }}
        />
      ))}

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <Fade in={showContent} timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            {/* 🏢 Logo/Brand */}
            <Zoom in={showContent} timeout={1000} style={{ transitionDelay: '200ms' }}>
              <Box
                sx={{
                  width: { xs: 80, md: 120 },
                  height: { xs: 80, md: 120 },
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  animation: `${pulseGlow} 3s ease-in-out infinite`,
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
              >
                <LoginIcon sx={{ fontSize: { xs: 40, md: 60 }, color: 'white' }} />
              </Box>
            </Zoom>

            {/* 🎨 Animated title */}
            <Typography
              variant="h2"
              sx={{
                color: 'white',
                fontWeight: 800,
                mb: 2,
                background: 'linear-gradient(45deg, #ffffff 30%, #f0f9ff 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 30px rgba(255,255,255,0.5)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)`,
                  animation: `${shimmerText} 3s ease-in-out infinite`,
                },
              }}
            >
              Invoice Factory
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)', 
                fontWeight: 400,
                mb: 4 
              }}
            >
              {czechTranslations.auth.login.subtitle}
            </Typography>
          </Box>
        </Fade>

        <Slide in={showForm} direction="up" timeout={800}>
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                {czechTranslations.auth.login.title}
              </Typography>
            </Box>

            {error && (
              <Grow in={!!error} timeout={500}>
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                      fontWeight: 500
                    }
                  }}
                >
                  {error}
                </Alert>
              </Grow>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Grow in={showForm} timeout={600} style={{ transitionDelay: '200ms' }}>
                <TextField
                  fullWidth
                  label={czechTranslations.auth.login.email}
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(99, 102, 241, 0.2)',
                      },
                    },
                  }}
                />
              </Grow>

              <Grow in={showForm} timeout={600} style={{ transitionDelay: '300ms' }}>
                <TextField
                  fullWidth
                  label={czechTranslations.auth.login.password}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(99, 102, 241, 0.2)',
                      },
                    },
                  }}
                />
              </Grow>

              <Grow in={showForm} timeout={600} style={{ transitionDelay: '400ms' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.rememberMe}
                      onChange={handleInputChange('rememberMe')}
                      color="primary"
                    />
                  }
                  label={czechTranslations.auth.login.rememberMe}
                  sx={{ mt: 2, mb: 2 }}
                />
              </Grow>

              <Grow in={showForm} timeout={600} style={{ transitionDelay: '500ms' }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mt: 2,
                    mb: 2,
                    py: 1.5,
                    borderRadius: 3,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transition: 'left 0.5s',
                    },
                    '&:hover::before': {
                      left: '100%',
                    },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)',
                    },
                  }}
                >
                  {loading ? (
                    <>{czechTranslations.common.loading.default}</>
                  ) : (
                    <>{czechTranslations.auth.login.loginButton}</>
                  )}
                </Button>
              </Grow>

              <Grow in={showForm} timeout={600} style={{ transitionDelay: '600ms' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => navigate('/forgot-password')}
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        textDecoration: 'underline',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {czechTranslations.auth.login.forgotPassword}
                  </Link>
                </Box>
              </Grow>

              <Grow in={showForm} timeout={600} style={{ transitionDelay: '700ms' }}>
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      nebo
                    </Typography>
                  </Divider>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {czechTranslations.auth.login.noAccount}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/register')}
                      startIcon={<RegisterIcon />}
                      sx={{
                        borderRadius: 3,
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      {czechTranslations.auth.login.register}
                    </Button>
                  </Box>
                </Box>
              </Grow>
            </Box>
          </Paper>
        </Slide>
      </Container>
    </Box>
  );
};

export default ModernLogin;
